/**
 * The ways a chord grid can be played, as data.
 *
 * A block chord says what the harmony is; a rhythm says what playing it sounds like. There is more
 * than one answer to that, and which one suits a chart is a listening choice rather than something
 * the grid states — so the patterns live here as a list to pick from rather than as a loop written
 * into the generator.
 *
 * A pattern is a **cycle of steps**. Each step says where it lands inside the cycle, which hand
 * plays it, and how long it sounds — all in beats, all written down. Nothing is derived: how long a
 * note is held is most of what separates one style from another, so it is stated rather than
 * inferred from the gap to the next one.
 *
 * Deliberately a leaf, like chordRhythm.js beside it. It knows beats and nothing else — no pitches,
 * no seconds, no chord symbols. chordBarsMidi.js imports this and resolves the hands to MIDI, so
 * this can't import that.
 */

import { FULL, NORMAL, SPARSE, STRONG } from './chartIntensity';
import { BEAT_EPSILON } from './chordBars';

/** A cycle as long as the bar, whatever the time signature makes that. */
export const BAR = 'bar';

/**
 * A cycle two bars long.
 *
 * Most of what a rhythm has to say fits in one bar and repeating it is the point. A few styles are
 * genuinely two bars, though — a salsa tumbao ties its last note over the bar line and leaves the
 * downbeat after it empty, and a pattern that restarted each bar could not leave that hole.
 */
export const TWO_BARS = 'two-bars';

/**
 * How fast and how busy a style is — the one thing every style here can be compared on.
 *
 * They are named for where they come from, and a Guajeo and a Güira say plenty to whoever already
 * plays salsa or merengue and nothing at all to anyone else. Pace is what the row of them is really
 * being read for: what a style will feel like under this chart, before it has been played.
 *
 * Written onto each pattern rather than worked out from its steps. Counting notes would file Güira
 * and Arpeggio together on sixteenths against eighths, and they are nothing alike to play against.
 */
const GENTLE = 'gentle';
const STEADY = 'steady';
const DRIVING = 'driving';

/**
 * The hands a step can be played by.
 *
 * `bass` and `arp` both walk a run rather than naming a pitch, and they differ in where the run
 * restarts. The bass carries on across the whole chord, which is what gives a pattern striking it
 * once a bar the root in one bar and its fifth in the next, for nothing. An arpeggio starts again
 * each cycle, so every bar rolls out from the bottom of the chord — a run that carried on would land
 * somewhere different each bar, since how many notes a chord has is not how many the bar asks for.
 *
 * `root` and `fifth` name the two notes of that same pair outright instead of walking it, which is
 * what a bass figure with a shape of its own needs: a tumbao puts the root on the beat it lands on
 * and the fifth on the one it pushes against, and letting a counter decide which is which would make
 * the figure come out backwards every other time round.
 */
export const BASS = 'bass';
export const CHORD = 'chord';
export const ARP = 'arp';
export const ROOT = 'root';
export const FIFTH = 'fifth';

/**
 * One note of the chord, asked for by degree: `tone: 0` is the root, 1 the third, 2 the fifth, 3 the
 * seventh — the chord's own intervals, counted in the order it stacks them.
 *
 * `arp` walks a run and `chord` plays the lot; neither can say *which* note it wants next. A guajeo
 * can: it goes root, third, fifth, seventh and then back to the third, and a hand that only knows
 * how to carry on would come back down through the fifth instead. So the figure names its notes.
 *
 * A degree the chord hasn't got wraps round to the bottom of it, which is what asking a triad for a
 * seventh should give you — the root over again, an octave up if the step is doubled. That is what a
 * player does with a guajeo over a triad rather than inventing a seventh the chart never wrote.
 *
 * `double: true` sounds the same note an octave above as well. Octaves are how this figure is
 * played — one line, thickened, rather than a chord — so it is a property of the step and not a
 * voicing decision made further down.
 *
 * `octave: -1` drops it an octave. A pedal is the one thing here that makes register matter: the
 * hand answering over a held root has to sit above that root and under the chord, and a note left in
 * the chord's own octave doubles the chord instead of answering it.
 *
 * `alter` bends the degree by semitones, which is how a figure reaches a note the chord hasn't got.
 * A merengue walks up into its top note from a whole step under it and through the semitone between
 * — `alter: -2` then `alter: -1` against the same degree — and those two are passing notes by
 * definition: a chord that contained them would not be leading anywhere. Written against the note
 * they arrive at rather than as pitches of their own, so the walk-up transposes with the chart.
 */
export const TONE = 'tone';

/**
 * A rhythm is a performance, so it is played like one.
 *
 * The block-chord preview is deliberately even — see VELOCITY in chordBarsMidi.js, where the point
 * is that a chart is being checked and an accent would be the preview inventing detail the grid
 * never stated. A rhythm has already made that choice: it is a reading of how to play the chart, and
 * a flat one is the thing that sounds mechanical. So a step may sit a little under the beat it
 * answers, which is all it takes to put the bass in front.
 */
const ANSWER = 0.85;

/**
 * A hold with no length of its own.
 *
 * getPatternSteps clips every hold to what is left of the chord, so a step written this way sounds
 * from where it is struck until the chord ends and not a beat further. That is a pedal: one low note
 * put down under the whole chord and left there while the hands move over it, which is most of what
 * separates a ballad from the same notes played square.
 */
const PEDAL = Infinity;

/**
 * LEAD: a step's `lead` field, for a figure that leans into the change.
 *
 * `lead: true` plays only in the chord's last cycle, and only when another chord follows straight
 * on, voiced from that next chord: a pickup on beat 4 into the next root. `lead: false` is what
 * plays there otherwise, so a chord held two bars carries on with its own figure through the first.
 * Only the piano reads it; a bass line or a strum with a lead step never plays it.
 */

/** A strum's direction, on a `strum` step that goes against the hand's own swing */
export const DOWN = 'down';
export const UP = 'up';

// Eight steps a bar, one per level, half a beat each
const eighthsOf = (step, levels) => levels.map((level, index) => ({ ...step, at: index / 2, hold: 0.5, level }));

/**
 * A pattern played with the damper down: every note goes on ringing until the chord changes.
 *
 * Not the same thing as PEDAL above, which is one note physically held. This is the foot — the
 * hands let go on time and the sound stays, so a figure can be sparse without being thin. It rides
 * on the `sustain` the playback path already reads off an uploaded clip's pedal, which means a note
 * still says how long it was *played* for and the on-screen keyboard still lifts its key on time.
 *
 * Lifted at every change, which is what a player does: holding it across one would smear two
 * harmonies into each other, and the chart is being played to hear the changes.
 */
const PEDALLED = true;

// Each pattern's `meters` lists the beats to the bar it is offered in. Most styles are written in
// 4/4 and fitted to a shorter bar by dropping what runs past its end, which leaves some still
// themselves and others chopped — and a longer bar would leave its last beats silent. A meter
// only goes on the list where what's left still sounds like the style; see fitsMeter below. The
// 3/4 and 6/8 styles at the end are written in their own bar, 6/8 as six eighths.
//
// Each pattern's `drums` names the groove played under it, by id, from drumGrooves.js. A string
// rather than the groove itself so this file stays a leaf: that one imports from here, not the
// other way round.
//
// A pattern with a `bass` line has a bass player: steps of the same shape on the same cycle, played
// on a bass guitar. While it plays, the piano drops its own left hand (LEFT_HAND below) and leaves
// the low end to it.
//
// A pattern's `pad` names the held chord that comes in under it from Strong up, by sound, from
// chordPad.js. A style a band wouldn't play a pad under leaves it off.
//
// A pattern's `band` lists the extra players it gets, by id out of BAND_LAYERS in chordBand.js: a
// brass section, an electric or an acoustic guitar, a violin. Each comes in at the levels the level table gives it.
// A guitar strums the pattern's `strum`, chord steps of its own on the same cycle, or the piano's
// chord steps where there is none. Its hand swings down on the beat and up on the &, which a step's
// `dir` can go against.
//
// `sparse`, where a style has one, is a whole figure of its own for Sparse, in place of thinning
// `steps`, for a style thinning leaves as busy as it was.
export const RHYTHM_PATTERNS = [
  {
    id: 'alternating',
    name: 'Alternating',
    description: 'A bass on every beat, the chord answering off it',
    cycle: 1,
    meters: [2, 3, 4],
    pace: STEADY,
    drums: 'pop',
    steps: [
      { at: 0, play: BASS, hold: 0.5 },
      { at: 0.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
  },
  {
    id: 'march',
    name: 'March',
    description: 'Oom-pah — the bass on the odd beats, the chord on the even ones',
    cycle: BAR,
    meters: [2, 4],
    pace: STEADY,
    drums: 'march',
    steps: [
      { at: 0, play: BASS, hold: 1 },
      { at: 1, play: CHORD, hold: 1, level: ANSWER },
      { at: 2, play: BASS, hold: 1 },
      { at: 3, play: CHORD, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'vals',
    name: 'Vals',
    description: 'The bass on the downbeat and the chord on every beat after it',
    cycle: BAR,
    meters: [3],
    pace: STEADY,
    drums: 'vals',
    pad: 'strings',
    band: ['violin'],
    steps: [
      { at: 0, play: BASS, hold: 1 },
      { at: 1, play: CHORD, hold: 1, level: ANSWER },
      { at: 2, play: CHORD, hold: 1, level: ANSWER },
      { at: 3, play: CHORD, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'cumbia',
    name: 'Cumbia',
    description: 'A bass every other beat, the chord on the offbeats around it',
    cycle: BAR,
    meters: [2, 4],
    pace: STEADY,
    drums: 'cumbia',
    steps: [
      { at: 0, play: BASS, hold: 0.5 },
      { at: 0.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: BASS, hold: 0.5 },
      { at: 2.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 3.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
  },
  {
    id: 'bolero',
    name: 'Bolero',
    // The cinquillo: five chord hits across the bar, on beats 1, 2, the & of 2, the & of 3 and 4.
    // Written out as eighths it is the figure the whole style is built on.
    description: 'The cinquillo — five chord hits across the bar over a bass on the odd beats',
    cycle: BAR,
    meters: [4],
    pace: GENTLE,
    drums: 'bolero',
    pad: 'strings',
    band: ['violin'],
    steps: [
      { at: 0, play: BASS, hold: 1 },
      { at: 0, play: CHORD, hold: 1, level: 0.9 },
      { at: 1, play: CHORD, hold: 0.5, level: 0.8 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: BASS, hold: 0.5 },
      { at: 2.5, play: CHORD, hold: 0.5, level: 0.8 },
      { at: 3, play: CHORD, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'salsa',
    name: 'Salsa',
    // The tumbao is genuinely two bars: the root on 4 is held over the bar line and the downbeat
    // after it is left empty, so the figure leans across the join instead of restarting at it. That
    // hole is the whole feel — a one-bar version of this is the Montuno beside it.
    description: 'A two-bar tumbao — root, the & of 2, then 4 tied over the bar line — under a montuno',
    cycle: TWO_BARS,
    meters: [4],
    pace: DRIVING,
    drums: 'son',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 1, play: ARP, hold: 0.5, level: ANSWER },
      { at: 1.5, play: FIFTH, hold: 0.5 },
      { at: 2, play: ARP, hold: 0.5, level: ANSWER },
      { at: 2.5, play: ARP, hold: 0.5, level: ANSWER },
      // A beat and a half, so it is still sounding on the far side of the bar line rather than
      // stopping politely at it — the tie is the figure, not a rest dressed up as one
      { at: 3, play: ROOT, hold: 1.5 },
      { at: 3.5, play: ARP, hold: 0.5, level: ANSWER },
      // Nothing struck on the second bar's downbeat: the root above is still ringing through it
      { at: 4.5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 5.5, play: FIFTH, hold: 0.5 },
      { at: 6, play: ARP, hold: 0.5, level: ANSWER },
      { at: 6.5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 7, play: ROOT, hold: 1.5 },
      { at: 7.5, play: ARP, hold: 0.5, level: ANSWER },
    ],
    // The tumbao, on the bass, held the way a bassist rings it into the next note
    bass: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 1.5, play: FIFTH, hold: 1.5, level: 0.85 },
      { at: 3, play: ROOT, hold: 2.5 },
      { at: 5.5, play: FIFTH, hold: 1.5, level: 0.85 },
      { at: 7, play: ROOT, hold: 1.5 },
    ],
  },
  {
    id: 'montuno',
    name: 'Montuno',
    // The same bass as the Salsa above, restated every bar and answered in chords rather than in
    // single notes. Fuller and squarer, and the one to reach for over a chart that changes fast:
    // a two-bar figure has nothing to lean across when the chord is gone by the second bar.
    description: 'The tumbao restated every bar, answered in full chords on the eighths',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'son',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 0.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 1, play: CHORD, hold: 0.5, level: 0.78 },
      { at: 1.5, play: FIFTH, hold: 0.5 },
      { at: 2, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2.5, play: CHORD, hold: 0.5, level: 0.78 },
      { at: 3, play: ROOT, hold: 0.5 },
      { at: 3.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
    // The same tumbao restated every bar
    bass: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 1.5, play: FIFTH, hold: 1.5, level: 0.85 },
      { at: 3, play: ROOT, hold: 1 },
    ],
  },
  {
    id: 'guajeo',
    name: 'Guajeo',
    // Transcribed off a played salsa piano part rather than built up from a description of one.
    // What it is worth having for is the turn-over: the first bar lands on the downbeat and leaves
    // the & of 1 alone, the second does exactly the opposite. That swap is the clave underneath it,
    // and it is the reason the figure has to be two bars long to be itself at all.
    description: 'A two-bar montuno that lands on the downbeat, then turns over and leaves it empty',
    cycle: TWO_BARS,
    meters: [4],
    pace: DRIVING,
    drums: 'son',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 1, play: ARP, hold: 0.5, level: ANSWER },
      { at: 1.5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 2, play: FIFTH, hold: 0.5 },
      { at: 2, play: ARP, hold: 0.5, level: ANSWER },
      { at: 2.5, play: ARP, hold: 0.5, level: 0.78 },
      { at: 3, play: ARP, hold: 0.5, level: ANSWER },
      { at: 3.5, play: ARP, hold: 0.5, level: 0.78 },
      // and the same bar the other way up — the downbeat left alone, the & of 1 taking it
      { at: 4.5, play: ROOT, hold: 0.5 },
      { at: 4.5, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 5.5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 6, play: FIFTH, hold: 0.5 },
      { at: 6, play: ARP, hold: 0.5, level: ANSWER },
      { at: 7, play: ARP, hold: 0.5, level: ANSWER },
      { at: 7.5, play: ARP, hold: 0.5, level: 0.78 },
    ],
    // On 1 and 3 in the first bar, then off the & of 1 in the second, with the turn-over
    bass: [
      { at: 0, play: ROOT, hold: 2 },
      { at: 2, play: FIFTH, hold: 2.5, level: 0.85 },
      { at: 4.5, play: ROOT, hold: 1.5 },
      { at: 6, play: FIFTH, hold: 2, level: 0.85 },
    ],
  },
  {
    id: 'tumbao',
    name: 'Tumbao',
    // The other figure out of that same performance, from where it thins out. The bass is the whole
    // point: it lands on 1 and 3 and then pushes off each of them half a beat later, so it is never
    // where the beat is for long. Sparser than the Guajeo, and the one that survives a slow tempo.
    description: 'A bass landing on 1 and 3 and pushing off each, under a thinner chord figure',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'son',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 1, play: CHORD, hold: 0.5, level: 0.78 },
      { at: 1.5, play: FIFTH, hold: 0.5 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: ROOT, hold: 0.5 },
      { at: 2, play: CHORD, hold: 0.5, level: 0.78 },
      { at: 3, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 3.5, play: FIFTH, hold: 0.5 },
    ],
    // On 1 and 3, pushing off each
    bass: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 1.5, play: FIFTH, hold: 0.5, level: 0.85 },
      { at: 2, play: ROOT, hold: 1.5 },
      { at: 3.5, play: FIFTH, hold: 0.5, level: 0.85 },
    ],
  },
  {
    id: 'merengue',
    name: 'Merengue',
    // Every eighth filled, which is what makes it drive. What keeps it from being the Alternating
    // at the top of the list is where the bass sits: on 1 and 3 it lands with the beat, on the & of
    // 2 and the & of 4 it pushes off it, and the last of those arrives early for the bar after.
    // Trading the two hands off the beat rather than on it is the lift the whole style runs on.
    description: 'Driving eighths, the bass pushing off the beat and into the next bar',
    cycle: BAR,
    meters: [2, 4],
    pace: DRIVING,
    drums: 'merengue',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 1, play: CHORD, hold: 0.5, level: 0.78 },
      { at: 1.5, play: FIFTH, hold: 0.5 },
      { at: 2, play: ROOT, hold: 0.5 },
      { at: 2.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 3, play: CHORD, hold: 0.5, level: 0.78 },
      { at: 3.5, play: FIFTH, hold: 0.5 },
    ],
    // With the beat on 1 and 3, pushing off it on the & of 2 and 4
    bass: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 1.5, play: FIFTH, hold: 0.5, level: 0.85 },
      { at: 2, play: ROOT, hold: 1 },
      { at: 3.5, play: FIFTH, hold: 0.5, level: 0.85 },
    ],
  },
  {
    id: 'jaleo',
    name: 'Jaleo',
    // Merengue's call and response: the first half of the bar holds, the second breaks into a run.
    // The one pattern here whose two halves are deliberately unlike each other, which is why it
    // wants a chord with room to sit — over a bar of fast changes it never reaches its answer.
    description: 'A held call across the first half of the bar, answered by a run across the second',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'merengue',
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 0, play: CHORD, hold: 1, level: 0.9 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: FIFTH, hold: 0.5 },
      { at: 2.5, play: ARP, hold: 0.5, level: ANSWER },
      { at: 3, play: ARP, hold: 0.5, level: ANSWER },
      { at: 3.5, play: ARP, hold: 0.5, level: ANSWER },
    ],
    // Held under the call, then the fifth under the run
    bass: [
      { at: 0, play: ROOT, hold: 2 },
      { at: 2, play: FIFTH, hold: 2, level: 0.85 },
    ],
  },
  {
    id: 'charleston',
    name: 'Charleston',
    // The last hit belongs to the bar after it: landing on the & of 4 it arrives early, which is
    // the whole point of the figure. Held short so the next bar's downbeat still lands clean.
    description: 'The chord on 1 and the & of 2, with a hit anticipating the next bar',
    cycle: BAR,
    meters: [4],
    pace: STEADY,
    drums: 'charleston',
    steps: [
      { at: 0, play: BASS, hold: 1 },
      { at: 0, play: CHORD, hold: 1.5, level: 0.9 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: BASS, hold: 1 },
      { at: 3.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
  },
  {
    id: 'arpeggio',
    name: 'Arpeggio',
    // Each tone rings four times as long as the gap to the next, so the roll piles up into the
    // chord instead of clicking past it — the one pattern here that is meant to overlap itself.
    // Eight steps to a bar against a triad's six-note run is why the run restarts each cycle: see
    // the note on ARP above.
    description: 'A bass on the downbeat, then the chord rolled out one note at a time',
    cycle: BAR,
    meters: [2, 3, 4],
    pace: GENTLE,
    drums: 'arpeggio',
    pad: 'warm',
    band: ['violin'],
    // At Sparse the roll slows to quarters
    sparse: [
      { at: 0, play: BASS, hold: 2 },
      { at: 1, play: ARP, hold: 2, level: 0.8 },
      { at: 2, play: ARP, hold: 2, level: 0.8 },
      { at: 3, play: ARP, hold: 2, level: 0.8 },
    ],
    steps: [
      { at: 0, play: BASS, hold: 2 },
      { at: 0.5, play: ARP, hold: 2, level: 0.8 },
      { at: 1, play: ARP, hold: 2, level: 0.8 },
      { at: 1.5, play: ARP, hold: 2, level: 0.8 },
      { at: 2, play: ARP, hold: 2, level: 0.8 },
      { at: 2.5, play: ARP, hold: 2, level: 0.8 },
      { at: 3, play: ARP, hold: 2, level: 0.8 },
      { at: 3.5, play: ARP, hold: 2, level: 0.8 },
    ],
  },
  {
    id: 'balada',
    name: 'Balada',
    // Transcribed off a played worship ballad. The figure itself is the March further up the list;
    // what makes it a different thing to listen to is the pedal under it, which never lifts, so the
    // chord on 1 and 3 lands on something already ringing instead of into silence.
    description: 'A root held under the whole chord, the chord on 1 and 3 and its fifth on 2 and 4',
    cycle: TWO_BARS,
    meters: [2, 4],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'balada',
    pad: 'warm',
    band: ['acoustic', 'violin'],
    // The ballad strum, D D-U -U D-U a bar, the ups from Normal so Soft rings the downs
    strum: [0, 4].flatMap((bar) => [
      { at: bar, play: CHORD, hold: 1, level: 0.9 },
      { at: bar + 1, play: CHORD, hold: 0.5, level: 0.6 },
      { at: bar + 1.5, play: CHORD, hold: 1, level: 0.7, minLevel: NORMAL },
      { at: bar + 2.5, play: CHORD, hold: 0.5, level: 0.7, minLevel: NORMAL },
      { at: bar + 3, play: CHORD, hold: 0.5, level: 0.65 },
      { at: bar + 3.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: NORMAL },
    ]),
    // At Sparse, the chord once a bar over the pedal
    sparse: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 4, level: 0.9 },
      { at: 4, play: CHORD, hold: 4, level: 0.9 },
    ],
    // At Full the right hand fills in, the chord softly on every &
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 1, level: 0.9 },
      { at: 0.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 1, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
      { at: 1.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 2, play: CHORD, hold: 1, level: ANSWER },
      { at: 2.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 3, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
      { at: 3.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 4, play: CHORD, hold: 1, level: 0.9 },
      { at: 4.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 5, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
      { at: 5.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 6, play: CHORD, hold: 1, level: ANSWER },
      { at: 6.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
      { at: 7, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
      { at: 7.5, play: CHORD, hold: 0.5, level: 0.6, minLevel: FULL },
    ],
  },
  {
    id: 'majestad',
    name: 'Majestad',
    // Transcribed off a played Cuán Grande Es Dios. Up to Normal it is the verse: Balada's figure,
    // but with the thumb on 2 and 4 played at about two thirds of the chord, as the take has it.
    // From Strong it is the chorus: eighths over the pedal, the fifth below on the &, the root on
    // the beat and a fifth-and-top pair on the & after it, which is where the accent falls.
    description: 'A held root, the chord on 1 and 3; from Strong, eighths pushing to an accent on the &',
    cycle: TWO_BARS,
    meters: [4],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'altar',
    pad: 'warm',
    band: ['violin'],
    // At Sparse, the chord held from 1 and from 3 over the pedal, as the take's intro opens
    sparse: [
      { at: 0, play: ROOT, hold: PEDAL },
      ...[0, 2, 4, 6].map((at) => ({ at, play: CHORD, hold: 2, level: at % 4 ? 0.8 : 0.9 })),
    ],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL, level: 0.9 },
      ...[0, 4].flatMap((bar) => [
        { at: bar, play: CHORD, hold: 1, level: 0.9, maxLevel: NORMAL },
        { at: bar + 1, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.55, maxLevel: NORMAL },
        { at: bar + 2, play: CHORD, hold: 1, level: 0.88, maxLevel: NORMAL },
        { at: bar + 3, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.55, maxLevel: NORMAL },
      ]),
      // The chorus figure. Only the first bar strikes the chord on 1; in the second the & of 4
      // before it is still ringing.
      { at: 0, play: CHORD, hold: 0.5, level: 0.9, minLevel: STRONG },
      ...[0, 2, 4, 6].flatMap((half) => [
        { at: half + 0.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.65, minLevel: STRONG },
        { at: half + 1, play: TONE, tone: 0, hold: 1, level: 0.8, minLevel: STRONG },
        { at: half + 1.5, play: TONE, tone: 2, hold: 1, level: 0.95, minLevel: STRONG },
        // The root on the first &, the third on the second, as the take's F plays C–F then C–A
        {
          at: half + 1.5,
          play: TONE,
          tone: half % 4 ? 1 : 0,
          octave: 1,
          hold: 1,
          level: 0.95,
          minLevel: STRONG,
        },
      ]),
    ],
  },
  {
    id: 'adoracion',
    name: 'Adoración',
    // The same pedal, with everything above it moved off the beat: the left hand answers on the &
    // and the chord lands on the & after that. The hit on the & of 4 belongs to the bar following
    // it, which is why the second bar's downbeat is empty — that note is still sounding through it.
    description: 'A held root under a syncopated figure that anticipates each bar',
    cycle: TWO_BARS,
    meters: [4],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'adoracion',
    pad: 'warm',
    band: ['violin'],
    // At Sparse, the chord on 1 and the anticipation into the second bar, held
    sparse: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 3.5, level: 0.9 },
      { at: 3.5, play: CHORD, hold: 4.5, level: ANSWER },
    ],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 0.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.8 },
      { at: 1, play: TONE, tone: 0, hold: 0.75, level: 0.78 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.8 },
      { at: 3, play: TONE, tone: 0, hold: 0.75, level: 0.78 },
      // A beat, so it is still ringing on the far side of the bar line — the anticipation is the
      // figure, and a hit that stopped at the bar line would just be an offbeat
      { at: 3.5, play: CHORD, hold: 1, level: ANSWER },
      // Nothing struck on the second bar's downbeat: the chord above is still sounding through it
      { at: 4.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.8 },
      { at: 5, play: TONE, tone: 0, hold: 0.75, level: 0.78 },
      { at: 5.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 6.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.8 },
      { at: 7, play: TONE, tone: 0, hold: 0.75, level: 0.78 },
    ],
  },
  {
    id: 'octavas',
    name: 'Octavas',
    // Transcribed off a played salsa piano part, which is why it names its notes rather than walking
    // them: it climbs root, third, fifth, seventh and then drops back to the third to climb again,
    // and the root and the seventh are struck in octaves while the two in between are not. The
    // Guajeo above it is the same idea in block chords; this is the thinner, harder-edged one.
    //
    // The part it came from was played over a band, so it carries no bass of its own. The tumbao
    // under it here is the one the Salsa above plays, which is what a bass does under this figure.
    description: 'A two-bar guajeo picked out in octaves, over a tumbao',
    cycle: TWO_BARS,
    meters: [4],
    pace: DRIVING,
    drums: 'son',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: TONE, tone: 0, hold: 0.5, double: true, level: 0.9 },
      { at: 0.5, play: TONE, tone: 1, hold: 0.75, level: ANSWER },
      { at: 1, play: TONE, tone: 2, hold: 0.5, level: 0.78 },
      { at: 1.5, play: FIFTH, hold: 0.5 },
      { at: 1.5, play: TONE, tone: 3, hold: 1, double: true, level: ANSWER },
      { at: 2.5, play: TONE, tone: 1, hold: 0.75, level: ANSWER },
      { at: 3, play: ROOT, hold: 1.5 },
      { at: 3, play: TONE, tone: 2, hold: 0.5, level: 0.78 },
      { at: 3.5, play: TONE, tone: 0, hold: 1, double: true, level: 0.9 },
      // and the second bar with its downbeat left empty, both hands leaning across the join
      { at: 4.5, play: TONE, tone: 1, hold: 0.75, level: ANSWER },
      { at: 5, play: TONE, tone: 2, hold: 0.5, level: 0.78 },
      { at: 5.5, play: FIFTH, hold: 0.5 },
      { at: 5.5, play: TONE, tone: 3, hold: 1, double: true, level: ANSWER },
      { at: 6.5, play: TONE, tone: 1, hold: 0.75, level: ANSWER },
      { at: 7, play: ROOT, hold: 1.5 },
      { at: 7, play: TONE, tone: 2, hold: 0.5, level: 0.78 },
    ],
    // The Salsa's tumbao under it, as the left hand had it
    bass: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 1.5, play: FIFTH, hold: 1.5, level: 0.85 },
      { at: 3, play: ROOT, hold: 2.5 },
      { at: 5.5, play: FIFTH, hold: 1.5, level: 0.85 },
      { at: 7, play: ROOT, hold: 1.5 },
    ],
  },
  {
    id: 'suave',
    name: 'Suave',
    // The Balada with the chord taken out of it: one note at a time over the same pedal, the third
    // on 1 and the root on 3. Nothing here would carry a chart on its own without the damper down —
    // which is the point of it, and why this is the one that wants a slow tempo and room.
    description: 'A single line over a held root — the third on 1, the root on 3',
    cycle: TWO_BARS,
    meters: [2, 4],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'suave',
    pad: 'warm',
    band: ['violin'],
    // At Sparse, the third and then the root, a bar each
    sparse: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: TONE, tone: 1, hold: 4, level: 0.9 },
      { at: 4, play: TONE, tone: 0, hold: 4, level: 0.9 },
    ],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: TONE, tone: 1, hold: 0.5, level: 0.9 },
      { at: 1, play: TONE, tone: 2, octave: -1, hold: 0.75, level: ANSWER },
      { at: 2, play: TONE, tone: 0, hold: 0.5, level: 0.9 },
      { at: 3, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
      { at: 4, play: TONE, tone: 1, hold: 0.5, level: 0.9 },
      { at: 5, play: TONE, tone: 2, octave: -1, hold: 0.75, level: ANSWER },
      { at: 6, play: TONE, tone: 0, hold: 0.5, level: 0.9 },
      { at: 7, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'guira',
    name: 'Güira',
    // Transcribed off a played merengue piano part, and the densest thing in this list: sixteenths,
    // with three registers trading rather than two. The fifth doubled at the octave lands on 1 and
    // 3, the chord answers it a sixteenth later, and a pair off the top of the chord takes the gaps
    // between — none of which is a hand this could name before a step could ask for a degree.
    description: 'Sixteenths at a run, three registers trading — the top in octaves',
    cycle: BAR,
    meters: [2, 4],
    pace: DRIVING,
    drums: 'merengue',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: TONE, tone: 2, octave: 1, double: true, hold: 0.5, level: 0.9 },
      { at: 0.25, play: CHORD, hold: 0.25, level: 0.78 },
      { at: 0.75, play: TONE, tone: 0, octave: 1, hold: 0.25, level: ANSWER },
      { at: 0.75, play: TONE, tone: 1, octave: 1, hold: 0.25, level: ANSWER },
      { at: 1.25, play: TONE, tone: 0, octave: 1, hold: 0.25, level: ANSWER },
      { at: 1.25, play: TONE, tone: 1, octave: 1, hold: 0.25, level: ANSWER },
      { at: 1.5, play: CHORD, hold: 0.25, level: 0.78 },
      { at: 2, play: FIFTH, hold: 0.5 },
      { at: 2, play: TONE, tone: 2, octave: 1, double: true, hold: 0.5, level: 0.9 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.78 },
      { at: 3, play: TONE, tone: 0, octave: 1, hold: 0.5, level: ANSWER },
      { at: 3, play: TONE, tone: 1, octave: 1, hold: 0.5, level: ANSWER },
      { at: 3.5, play: CHORD, hold: 0.25, level: 0.78 },
    ],
    // On 1 and 3 under the sixteenths
    bass: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 2, play: FIFTH, hold: 1.5, level: 0.85 },
    ],
  },
  {
    id: 'cadena',
    name: 'Cadena',
    // The other merengue out of that pair, and the reason `alter` exists. Twice a bar the top voice
    // walks up into the same note from a whole step under it — a step, then the semitone, then the
    // note itself on the beat. The chain is the figure; the chord underneath is only there to be
    // arrived at, which is why it answers on the offbeats and never on 1 or 3.
    description: 'A chromatic walk-up into the top note, twice a bar, over an offbeat chord',
    cycle: BAR,
    meters: [2, 4],
    pace: DRIVING,
    drums: 'merengue',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: TONE, tone: 2, octave: 1, hold: 0.5, level: 0.9 },
      { at: 0.25, play: TONE, tone: 0, hold: 0.5, level: 0.78 },
      { at: 0.5, play: TONE, tone: 1, hold: 0.25, level: 0.78 },
      // A whole step under the note it is heading for, then the semitone between — neither of them
      // in the chord, both of them written against the degree they arrive at
      { at: 1, play: TONE, tone: 2, octave: 1, alter: -2, hold: 0.5, level: ANSWER },
      { at: 1.5, play: TONE, tone: 2, octave: 1, alter: -1, hold: 0.25, level: ANSWER },
      { at: 1.5, play: CHORD, hold: 0.25, level: 0.78 },
      { at: 2, play: FIFTH, hold: 0.5 },
      { at: 2, play: TONE, tone: 2, octave: 1, hold: 0.5, level: 0.9 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.78 },
      { at: 3, play: TONE, tone: 2, octave: 1, alter: -2, hold: 0.5, level: ANSWER },
      { at: 3.5, play: TONE, tone: 2, octave: 1, alter: -1, hold: 0.25, level: ANSWER },
      { at: 3.5, play: CHORD, hold: 0.25, level: 0.78 },
    ],
    // On 1 and 3, out of the way of the walk-up
    bass: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 2, play: FIFTH, hold: 1.5, level: 0.85 },
    ],
  },
  // Worship and praise styles in 4/4, written from standard patterns rather than transcribed, each
  // over a groove of its own. Their 3/4 and 6/8 counterparts are at the end with the others.
  {
    id: 'pulso',
    name: 'Pulso',
    // The chord in even eighths over a held root, accented three, three and two — the build a
    // worship set climbs on before the band comes in
    description: 'A held root under the chord pulsing in eighths, leaning on 1, the & of 2 and 4',
    cycle: BAR,
    meters: [4],
    pace: STEADY,
    pedal: PEDALLED,
    drums: 'pulso',
    pad: 'warm',
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      // The soft chord on the half bar would survive thinning, on a strong beat, and blur the 3-3-2
      ...[0.9, 0.6, 0.65, 0.85, 0.65, 0.6, 0.85, 0.6].map((level, index) => ({
        at: index / 2,
        play: CHORD,
        hold: 0.5,
        level,
        ...(index === 4 && { minLevel: NORMAL }),
      })),
    ],
  },
  {
    id: 'rocio',
    name: 'Rocío',
    // The same three, three and two, as three held chords instead of a pulse, and a high third
    // leading on into the next bar
    description: 'A held root under the chord on 1, the & of 2 and 4, a high third leading on',
    cycle: BAR,
    meters: [4],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'rocio',
    pad: 'warm',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 1.5, level: 0.9 },
      { at: 1.5, play: CHORD, hold: 1.5, level: ANSWER },
      { at: 3, play: CHORD, hold: 1, level: 0.8 },
      { at: 3.5, play: TONE, tone: 1, octave: 1, hold: 0.5, level: 0.7 },
    ],
  },
  {
    id: 'ofrenda',
    name: 'Ofrenda',
    // The sparest of them: the chord laid down once a bar and left, and a few high notes of it
    // answering on 3 and 4 the way a pad leaves room for a line over it
    description: 'The chord held the whole bar over a held root, a few high notes answering it',
    cycle: TWO_BARS,
    meters: [4],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'ofrenda',
    pad: 'warm',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 4, level: 0.8 },
      { at: 2, play: TONE, tone: 1, octave: 1, hold: 1, level: 0.7 },
      { at: 3, play: TONE, tone: 2, octave: 1, hold: 1, level: 0.65 },
      { at: 4, play: CHORD, hold: 2, level: 0.7 },
      { at: 6, play: TONE, tone: 0, octave: 1, hold: 2, level: 0.7 },
    ],
  },
  {
    id: 'victoria',
    name: 'Victoria',
    // The bass pumping the root in eighths, and the chord on 1, 2, the & of 2 held into 3, then the
    // & of 3, 4 and its &
    description: 'The bass pumping eighths under a pushing chord, for four-on-the-floor praise',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'victoria',
    pad: 'pulse',
    band: ['guitar'],
    // Eighth chugs, opening on the pushes: 1, the & of 2 and the & of 3
    strum: eighthsOf({ play: CHORD }, [1, 0.6, 0.7, 0.9, 0.6, 0.85, 0.7, 0.6]),
    steps: [
      { at: 0, play: ROOT, hold: 0.5, level: 0.85 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 0.5, play: ROOT, hold: 0.5, level: 0.65 },
      { at: 1, play: ROOT, hold: 0.5, level: 0.8 },
      { at: 1, play: CHORD, hold: 0.5, level: 0.75 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.65 },
      { at: 1.5, play: CHORD, hold: 1, level: 0.85 },
      { at: 2, play: ROOT, hold: 0.5, level: 0.85 },
      { at: 2.5, play: ROOT, hold: 0.5, level: 0.65 },
      { at: 2.5, play: CHORD, hold: 0.5, level: 0.7 },
      { at: 3, play: ROOT, hold: 0.5, level: 0.8 },
      { at: 3, play: CHORD, hold: 0.5, level: 0.75 },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.65 },
      { at: 3.5, play: CHORD, hold: 0.5, level: 0.85 },
    ],
  },
  // Heraldo, Espada and Estandarte were transcribed off Por la Espada, played over Victoria's drums
  // at 155 BPM (.claude/references/rhythm-midi/por la espada.mid): its intro, its groove and its hits.
  {
    id: 'heraldo',
    name: 'Heraldo',
    // Root, fifth, octave and tenth rolled up in eighths twice a bar, each left ringing into the next
    description: 'A low root under the chord rolled up root, fifth, octave, tenth in eighths',
    cycle: BAR,
    meters: [4],
    pace: STEADY,
    drums: 'victoria',
    pad: 'pulse',
    // At Sparse the roll slows to quarters
    sparse: [
      { at: 0, play: ROOT, hold: PEDAL, level: 0.75 },
      { at: 1, play: TONE, tone: 2, hold: 1.5, level: 0.75 },
      { at: 2, play: TONE, tone: 0, octave: 1, hold: 1.5, level: 0.75 },
      { at: 3, play: TONE, tone: 1, octave: 1, hold: 1, level: 0.75 },
    ],
    // From Strong the tenth joins the octave on the & of 2
    steps: [
      { at: 0, play: ROOT, hold: PEDAL, level: 0.75 },
      { at: 0.5, play: TONE, tone: 0, hold: 1.25, level: 0.72 },
      { at: 1, play: TONE, tone: 2, hold: 1, level: 0.78 },
      { at: 1.5, play: TONE, tone: 0, octave: 1, hold: 0.75, level: 0.8 },
      { at: 1.5, play: TONE, tone: 1, octave: 1, hold: 0.5, level: 0.8, minLevel: STRONG },
      { at: 2, play: TONE, tone: 0, hold: 1.5, level: 0.58 },
      { at: 2.5, play: TONE, tone: 2, hold: 1.5, level: 0.78 },
      { at: 3, play: TONE, tone: 0, octave: 1, hold: 1.25, level: 0.77 },
      { at: 3.5, play: TONE, tone: 1, octave: 1, hold: 0.5, level: 0.78 },
    ],
  },
  {
    id: 'espada',
    name: 'Espada',
    // The left hand bouncing the fifth below against the root in eighths, the chord on 1 and the &
    // of 2. On 4 and its & the last bar leads into the change: the next chord's root, then its third.
    description: 'A bouncing fifth and root under the chord, leading into each change on 4',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'victoria',
    pad: 'pulse',
    band: ['guitar'],
    strum: eighthsOf({ play: CHORD }, [1, 0.6, 0.7, 0.9, 0.6, 0.7, 0.8, 0.6]),
    // From Strong, as on the repeat: the chord on the & of 3 where the root was, and the pickup's
    // & thickened with the next chord's fifth
    steps: [
      { at: 0, play: ROOT, hold: 3, level: 0.8 },
      { at: 0, play: CHORD, hold: 1, level: 0.88 },
      { at: 0.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.75 },
      { at: 1, play: TONE, tone: 0, hold: 0.5, level: 0.8 },
      { at: 1.5, play: CHORD, hold: 0.5, level: 0.82 },
      { at: 2, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.75 },
      { at: 2.5, play: TONE, tone: 0, hold: 0.5, level: 0.83, maxLevel: NORMAL },
      { at: 2.5, play: CHORD, hold: 0.5, level: 0.8, minLevel: STRONG },
      { at: 3, play: TONE, tone: 2, octave: -1, hold: 0.5, level: 0.78, lead: false },
      { at: 3.5, play: TONE, tone: 0, hold: 0.5, level: 0.78, lead: false },
      { at: 3, play: TONE, tone: 0, hold: 0.75, level: 0.88, lead: true },
      { at: 3.5, play: TONE, tone: 1, hold: 0.5, level: 0.82, lead: true },
      { at: 3.5, play: TONE, tone: 2, hold: 0.5, level: 0.78, lead: true, minLevel: STRONG },
    ],
  },
  {
    id: 'estandarte',
    name: 'Estandarte',
    // The full chord over its root on every beat. Soft and Sparse keep only the downbeat, which is
    // how the song's break opens before the quarters come in.
    description: 'Hits: the chord over its root on every beat, one stab a bar when soft',
    cycle: BAR,
    meters: [2, 4],
    pace: DRIVING,
    drums: 'victoria',
    steps: [0, 1, 2, 3].flatMap((at) => [
      { at, play: ROOT, hold: 0.6, level: at === 0 ? 0.9 : 0.82, ...(at > 0 && { minLevel: NORMAL }) },
      { at, play: CHORD, hold: 0.6, level: at === 0 ? 0.92 : 0.85, ...(at > 0 && { minLevel: NORMAL }) },
    ]),
  },
  {
    id: 'aclamacion',
    name: 'Aclamación',
    // Half time: the chord and the bass together on 1, then pushed off the & of 2 and held, the
    // second bar landing its push a beat later to turn the phrase round
    description: 'A half-time anthem — chord and bass pushing off the & of 2 over a tom groove',
    cycle: TWO_BARS,
    meters: [4],
    pace: STEADY,
    drums: 'aclamacion',
    pad: 'warm',
    steps: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 0, play: CHORD, hold: 1.5, level: 0.9 },
      { at: 1.5, play: ROOT, hold: 2.5, level: ANSWER },
      { at: 1.5, play: CHORD, hold: 2.5, level: ANSWER },
      { at: 4, play: ROOT, hold: 1.5 },
      { at: 4, play: CHORD, hold: 1.5, level: 0.9 },
      { at: 5.5, play: FIFTH, hold: 1, level: ANSWER },
      { at: 5.5, play: CHORD, hold: 1, level: ANSWER },
      { at: 6.5, play: ROOT, hold: 1.5, level: 0.8 },
      { at: 6.5, play: CHORD, hold: 1.5, level: 0.8 },
    ],
  },
  {
    id: 'gloria',
    name: 'Gloria',
    // Stabs on the sixteenths either side of the beat, and the bass locked to the kick under them
    description: 'Chord stabs on the sixteenths around the beat, the bass locked to the kick — gospel praise',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'gloria',
    pad: 'organ',
    steps: [
      { at: 0, play: ROOT, hold: 0.75 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 0.75, play: ROOT, hold: 0.25, level: 0.7 },
      { at: 0.75, play: CHORD, hold: 0.25, level: 0.7 },
      { at: 1.5, play: CHORD, hold: 0.5, level: 0.85 },
      { at: 2, play: FIFTH, hold: 0.75 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.7 },
      { at: 2.75, play: ROOT, hold: 0.75, level: 0.8 },
      { at: 2.75, play: CHORD, hold: 0.5, level: 0.8 },
      { at: 3.5, play: CHORD, hold: 0.5, level: 0.85 },
    ],
  },
  {
    id: 'clamor',
    name: 'Clamor',
    // The big chorus of a worship set: the chord held over the pedal, and a melody over it in octaves
    // that a band would give to a lead line
    description: 'A held root and chord under a melody in octaves — the big worship chorus',
    cycle: BAR,
    meters: [4],
    pace: STEADY,
    pedal: PEDALLED,
    drums: 'clamor',
    pad: 'warm',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 2, level: 0.85 },
      { at: 0, play: TONE, tone: 0, octave: 1, double: true, hold: 1.5, level: 0.8 },
      { at: 1.5, play: TONE, tone: 1, octave: 1, double: true, hold: 1, level: 0.75 },
      { at: 2, play: CHORD, hold: 2, level: ANSWER },
      { at: 2.5, play: TONE, tone: 2, octave: 1, double: true, hold: 1, level: 0.75 },
      { at: 3.5, play: TONE, tone: 1, octave: 1, double: true, hold: 0.5, level: 0.7 },
    ],
  },
  {
    id: 'corito',
    name: 'Corito',
    // The polka a congregation claps a corito to: the bass on every beat, root then fifth, and the
    // chord short on every & between
    description: 'Root and fifth on every beat, the chord short on every & — the corito polka',
    cycle: BAR,
    meters: [2, 4],
    pace: STEADY,
    drums: 'corito',
    pad: 'organ',
    band: ['acoustic'],
    // Down and up on every beat, light on the beat and leaning on the & with the piano
    strum: eighthsOf({ play: CHORD }, [0.3, 0.8, 0.25, 0.75, 0.3, 0.8, 0.25, 0.75]),
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 1, play: FIFTH, hold: 0.5, level: 0.9 },
      { at: 1.5, play: CHORD, hold: 0.25, level: 0.75 },
      { at: 2, play: ROOT, hold: 0.5 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 3, play: FIFTH, hold: 0.5, level: 0.9 },
      { at: 3.5, play: CHORD, hold: 0.25, level: 0.75 },
    ],
  },
  {
    id: 'alegria',
    name: 'Alegría',
    // Corito on the piano alone: the beat taken out of the bass and played in octaves in the middle
    // of the keyboard, root then fifth, with the chord short on every & as before
    description: 'The corito polka on the piano alone — octaves on the beat, the chord on every &',
    cycle: BAR,
    meters: [2, 4],
    pace: STEADY,
    drums: 'corito',
    pad: 'organ',
    steps: [
      { at: 0, play: TONE, tone: 0, double: true, hold: 0.5 },
      { at: 0.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 1, play: TONE, tone: 2, double: true, hold: 0.5, level: 0.9 },
      { at: 1.5, play: CHORD, hold: 0.25, level: 0.75 },
      { at: 2, play: TONE, tone: 0, double: true, hold: 0.5 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 3, play: TONE, tone: 2, double: true, hold: 0.5, level: 0.9 },
      { at: 3.5, play: CHORD, hold: 0.25, level: 0.75 },
    ],
  },
  {
    id: 'contratiempo',
    name: 'Contratiempo',
    // Corito's right hand on its own, tapped in at 155: the chord short on every & and nothing on
    // the beat, which the kick keeps
    description: 'The chord short on every & and nothing on the beat — the corito right hand alone',
    cycle: BAR,
    meters: [2, 4],
    pace: STEADY,
    drums: 'corito',
    pad: 'organ',
    steps: [
      { at: 0.5, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 1.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 3.5, play: CHORD, hold: 0.25, level: 0.8 },
    ],
  },
  {
    id: 'brisa',
    name: 'Brisa',
    // Reggae praise: the chord on 2 and 4 with a pickup into the bar, and a bass that drops out on 3
    // where the one-drop's kick lands
    description: 'A reggae one-drop — the chord on 2 and 4, the bass leaving room on 3',
    cycle: BAR,
    meters: [4],
    pace: STEADY,
    drums: 'brisa',
    steps: [
      { at: 0, play: ROOT, hold: 0.75 },
      { at: 1, play: CHORD, hold: 0.5, level: 0.85 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 2.5, play: FIFTH, hold: 1, level: 0.8 },
      { at: 3, play: CHORD, hold: 0.5, level: 0.85 },
      { at: 3.75, play: CHORD, hold: 0.25, level: 0.6 },
    ],
  },
  {
    id: 'exalta',
    name: 'Exalta',
    // The bass on 1 and its & with the kicks, the chord stabbed with the snares on 2 and 4, and a
    // sixteenth push off the & of 2 riding the ghost notes into 3
    description: 'Latin praise — kick-kick-snare, then ghost notes up to the snare on 4',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'exalta',
    pad: 'bright',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 0.5, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 1, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 1.75, play: CHORD, hold: 0.25, level: 0.6 },
      { at: 2, play: FIFTH, hold: 0.5, level: 0.8 },
      { at: 2.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 3, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.7 },
    ],
    // With the kicks on 1 and its &, the fifth above on 3, back to the root off the & of 4
    bass: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: ROOT, hold: 1, level: 0.75 },
      { at: 2, play: TONE, tone: 2, hold: 1, level: 0.85 },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.7 },
    ],
  },
  {
    id: 'alabanza',
    name: 'Alabanza',
    // Pump-pump-stab twice a bar: the bass on each beat and its & with the kicks, the root then the
    // fifth, and the chord short with the snares on 2 and 4 plus a push off the last sixteenth
    description: 'Latin praise — kick-kick-snare twice a bar, the bass pumping with the kick',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'alabanza',
    pad: 'bright',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.85 },
      { at: 0.5, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 1, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 2, play: FIFTH, hold: 0.5, level: 0.85 },
      { at: 2.5, play: FIFTH, hold: 0.5, level: 0.7 },
      { at: 3, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 3.75, play: CHORD, hold: 0.25, level: 0.65 },
    ],
    // Pumping with the kick: the root on 1 and its &, the fifth above on 3 and its &
    bass: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: ROOT, hold: 1, level: 0.75 },
      { at: 2, play: TONE, tone: 2, hold: 0.5, level: 0.85 },
      { at: 2.5, play: TONE, tone: 2, hold: 1, level: 0.7 },
    ],
  },
  {
    id: 'hosanna',
    name: 'Hosanna',
    // The bass on 1 and 3 and the chord off the beat on every & — the push a Latin praise piano puts
    // over a busy kit, so it answers the drums rather than doubling them
    description: 'Latin praise — the chord on every & over sixteenth hats and ghost notes',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'hosanna',
    pad: 'bright',
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 0.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 1.5, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 2, play: FIFTH, hold: 1, level: 0.85 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 3.5, play: CHORD, hold: 0.25, level: 0.85 },
    ],
    // Locked to the kick on 1, its & and the & of 2, then the fifth above on 3
    bass: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: ROOT, hold: 0.75, level: 0.75 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.8 },
      { at: 2, play: TONE, tone: 2, hold: 1.5, level: 0.85 },
    ],
  },
  {
    id: 'maranata',
    name: 'Maranata',
    // Modelled on Cristo Viene: everything locked to a kick on 1, the & of 2, 3 and the & of 4. The
    // chord is short with the snares and pushed with the kicks; the bass player carries the low end.
    description: 'Latin praise with a bass player — the chord pushed with the kick, short with the snare',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'maranata',
    pad: 'bright',
    steps: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 1, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: FIFTH, hold: 0.5 },
      { at: 3, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 3.5, play: FIFTH, hold: 0.5, level: 0.75 },
      { at: 3.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
    // Root, root pushed off the & of 2, the fifth above on 3, and the octave pushing into the next bar
    bass: [
      { at: 0, play: ROOT, hold: 1.25 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 2, play: TONE, tone: 2, hold: 1.25, level: 0.9 },
      { at: 3.5, play: TONE, tone: 0, octave: 1, hold: 0.5, level: 0.75 },
    ],
  },
  // Latin pop-rock after Amo Tu Gloria, from a written description of the recording rather than a
  // played part: Fanfarria for its intro, Presencia for the bridge.
  {
    id: 'fanfarria',
    name: 'Fanfarria',
    // Short stabs on syncopated sixteenths, the way the keys double a horn riff, with the left hand
    // on 1 and 3 for when the bass player is off
    description: 'Latin pop-rock — syncopated stabs like a horn riff, congas on the &s of 1 and 3',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'fanfarria',
    band: ['brass'],
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 0, play: CHORD, hold: 0.25, level: 0.9 },
      { at: 0.75, play: CHORD, hold: 0.25, level: 0.7 },
      { at: 1.5, play: CHORD, hold: 0.75, level: ANSWER },
      { at: 2, play: FIFTH, hold: 1, level: 0.85 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.75 },
      { at: 3.25, play: CHORD, hold: 0.25, level: 0.7 },
      { at: 3.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
    // With the kick on 1 and the & of 2, the fifth above on 3, the root pushed off the & of 4
    bass: [
      { at: 0, play: ROOT, hold: 1.25 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 2, play: TONE, tone: 2, hold: 1.25, level: 0.85 },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.75 },
    ],
  },
  {
    id: 'presencia',
    name: 'Presencia',
    // Stabs that answer the congas rather than doubling the kick: short with the snares on 2 and 4,
    // pushed on the & of 2 and the & of 4, a sixteenth pickup into 4
    description: 'Latin pop-rock — sixteenth hats and congas, a timbale roll closing every four bars',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'presencia',
    pad: 'bright',
    band: ['guitar'],
    // Eighth chugs, opening with the stabs on 1, the & of 2 and the & of 4
    strum: eighthsOf({ play: CHORD }, [1, 0.6, 0.7, 0.9, 0.7, 0.6, 0.7, 0.9]),
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 0, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 1, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: FIFTH, hold: 1, level: 0.85 },
      { at: 2.75, play: CHORD, hold: 0.25, level: 0.65 },
      { at: 3, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 3.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
    // Driving eighths with the kick and the congas: the root on 1, its & and the & of 2, the fifth
    // above on 3 and its &, and the octave pushing into the next bar
    bass: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.5, play: ROOT, hold: 0.5, level: 0.6 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.8 },
      { at: 2, play: TONE, tone: 2, hold: 0.5, level: 0.9 },
      { at: 2.5, play: TONE, tone: 2, hold: 0.75, level: 0.6 },
      { at: 3.5, play: TONE, tone: 0, octave: 1, hold: 0.5, level: 0.75 },
    ],
  },
  {
    id: 'regocijo',
    name: 'Regocijo',
    // Off Gemini's example: short, bright stabs on sixteenths 0, 3, 6, 10, 12 and 14 against a
    // four-on-the-floor kick, so only 1 and 4 land with it
    description: 'Latin praise — four on the floor, the chord stabbed off the beat',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'regocijo',
    pad: 'bright',
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 0, play: CHORD, hold: 0.25, level: 0.9 },
      { at: 0.75, play: CHORD, hold: 0.25, level: 0.7 },
      { at: 1.5, play: CHORD, hold: 0.25, level: ANSWER },
      { at: 2, play: FIFTH, hold: 1, level: 0.85 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.75 },
      { at: 3, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.7 },
      { at: 3.5, play: CHORD, hold: 0.25, level: ANSWER },
    ],
    // Off the same example: short roots on sixteenths 0, 4, 8, 10 and 12, with the kick on the beats
    // and doubling up on the & of 3
    bass: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 1, play: ROOT, hold: 0.5, level: 0.75 },
      { at: 2, play: ROOT, hold: 0.5, level: 0.85 },
      { at: 2.5, play: ROOT, hold: 0.5, level: 0.7 },
      { at: 3, play: ROOT, hold: 0.5, level: 0.8 },
    ],
  },
  {
    id: 'exodo',
    name: 'Éxodo',
    // Off a generated example for "Abrió el Mar" at 120 BPM: stabs on sixteenths 2, 4, 7, 10 and 13,
    // never on the downbeat, which the bass takes. The left hand sits on the kick for when it's off.
    description: 'Latin praise — the chord stabbed around the beat, a bass walking up to the fifth',
    cycle: BAR,
    meters: [4],
    pace: DRIVING,
    drums: 'exodo',
    pad: 'bright',
    steps: [
      { at: 0, play: ROOT, hold: 1.5 },
      { at: 0.5, play: CHORD, hold: 0.25, level: 0.8 },
      { at: 1, play: CHORD, hold: 0.25, level: 0.9 },
      { at: 1.5, play: FIFTH, hold: 0.5, level: 0.75 },
      { at: 1.75, play: CHORD, hold: 0.25, level: 0.75 },
      { at: 2, play: ROOT, hold: 1.5, level: 0.85 },
      { at: 2.5, play: CHORD, hold: 0.25, level: 0.85 },
      { at: 3.25, play: CHORD, hold: 0.25, level: 0.7 },
      { at: 3.5, play: FIFTH, hold: 0.5, level: 0.75 },
    ],
    // Off the same example: root, the fifth anticipated, root, then a walk up the second and third
    // to the fifth and back. The second is a whole step over the root, so it holds on any chord.
    bass: [
      { at: 0, play: ROOT, hold: 0.5 },
      { at: 0.75, play: TONE, tone: 2, hold: 0.5, level: 0.75 },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.8 },
      { at: 2, play: TONE, tone: 0, alter: 2, hold: 0.5, level: 0.7 },
      { at: 2.5, play: TONE, tone: 1, hold: 0.5, level: 0.75 },
      { at: 3, play: TONE, tone: 2, hold: 0.5, level: 0.8 },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.75 },
    ],
  },
  {
    id: 'himno',
    name: 'Himno',
    // The Balada in three: the same held root, the chord on 1 and 3 and its fifth below on 2
    description: 'A 3/4 worship ballad — a held root, the chord on 1 and 3, its fifth on 2',
    cycle: BAR,
    meters: [3],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'himno',
    pad: 'church',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 1, level: 0.9 },
      { at: 1, play: TONE, tone: 2, octave: -1, hold: 1, level: ANSWER },
      { at: 2, play: CHORD, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'jubilo',
    name: 'Júbilo',
    description: '3/4 praise — the root on 1 and the fifth on 3, the chord moving in eighths over them',
    cycle: BAR,
    meters: [3],
    pace: STEADY,
    drums: 'jubilo',
    pad: 'strings',
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 0, play: CHORD, hold: 0.5, level: 0.9 },
      { at: 1, play: CHORD, hold: 0.5, level: 0.8 },
      { at: 1.5, play: CHORD, hold: 0.5, level: ANSWER },
      { at: 2, play: FIFTH, hold: 0.5 },
      { at: 2, play: CHORD, hold: 0.5, level: 0.8 },
      { at: 2.5, play: CHORD, hold: 0.5, level: ANSWER },
    ],
  },
  {
    id: 'mecida',
    name: 'Mecida',
    // Written in eighths, six to the bar. The broken chord climbs to the third on 4, the second half
    // of the bar's lilt, and the pedal lets the whole run pile up into the chord.
    description: 'A 6/8 worship ballad — the chord broken one eighth at a time over a held root',
    cycle: BAR,
    meters: [6],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'mecida',
    pad: 'warm',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 1, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.8 },
      { at: 2, play: TONE, tone: 0, hold: 1, level: 0.8 },
      { at: 3, play: TONE, tone: 1, hold: 1, level: 0.9 },
      { at: 4, play: TONE, tone: 2, hold: 1, level: 0.8 },
      { at: 5, play: TONE, tone: 0, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'gozo',
    name: 'Gozo',
    // In eighths too: the bass on each half of the bar, each chord hit answered two eighths later
    description: '6/8 praise — the root and fifth on each half of the bar, the chord answering after each',
    cycle: BAR,
    meters: [6],
    pace: STEADY,
    drums: 'gozo',
    pad: 'strings',
    steps: [
      { at: 0, play: ROOT, hold: 2 },
      { at: 0, play: CHORD, hold: 1, level: 0.9 },
      { at: 2, play: CHORD, hold: 1, level: ANSWER },
      { at: 3, play: FIFTH, hold: 2 },
      { at: 3, play: CHORD, hold: 1, level: 0.85 },
      { at: 5, play: CHORD, hold: 1, level: ANSWER },
    ],
  },
  {
    id: 'reposo',
    name: 'Reposo',
    // Himno's held root with the chord rolled up over it in eighths instead of struck
    description: 'A 3/4 worship ballad — the chord rolled up in eighths over a held root',
    cycle: BAR,
    meters: [3],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'reposo',
    pad: 'warm',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0.5, play: TONE, tone: 2, octave: -1, hold: 1, level: 0.7 },
      { at: 1, play: TONE, tone: 0, hold: 1, level: 0.75 },
      { at: 1.5, play: TONE, tone: 1, hold: 1, level: 0.8 },
      { at: 2, play: TONE, tone: 2, hold: 1, level: 0.75 },
      { at: 2.5, play: TONE, tone: 1, hold: 0.5, level: 0.65 },
    ],
  },
  {
    id: 'quietud',
    name: 'Quietud',
    // In eighths: the chord laid down on 1 and left, and two high notes answering on 4 and 6
    description: '6/8 worship — the chord held on 1 over a held root, two high notes answering on 4 and 6',
    cycle: BAR,
    meters: [6],
    pace: GENTLE,
    pedal: PEDALLED,
    drums: 'quietud',
    pad: 'warm',
    band: ['violin'],
    steps: [
      { at: 0, play: ROOT, hold: PEDAL },
      { at: 0, play: CHORD, hold: 3, level: 0.8 },
      { at: 3, play: TONE, tone: 2, octave: 1, hold: 2, level: 0.7 },
      { at: 5, play: TONE, tone: 1, octave: 1, hold: 1, level: 0.65 },
    ],
  },
  {
    id: 'pandero',
    name: 'Pandero',
    // In eighths: an oom-pa-pa on each half of the bar, the root and then the fifth under it
    description: '6/8 praise — the bass on each half of the bar, the chord on the two eighths after it',
    cycle: BAR,
    meters: [6],
    pace: DRIVING,
    drums: 'pandero',
    pad: 'organ',
    steps: [
      { at: 0, play: ROOT, hold: 1 },
      { at: 1, play: CHORD, hold: 0.5, level: 0.75 },
      { at: 2, play: CHORD, hold: 0.5, level: 0.7 },
      { at: 3, play: FIFTH, hold: 1, level: 0.9 },
      { at: 4, play: CHORD, hold: 0.5, level: 0.75 },
      { at: 5, play: CHORD, hold: 0.5, level: 0.7 },
    ],
  },
];

/** What Rhythm plays until someone picks otherwise — the first one there was. */
export const DEFAULT_RHYTHM_PATTERN_ID = RHYTHM_PATTERNS[0].id;

/**
 * One pattern by id.
 *
 * An id it doesn't know falls back to the default rather than coming back empty: the choice is
 * stored between sessions, so a build that drops or renames a style would otherwise leave whoever
 * had it selected with a Rhythm button that plays nothing.
 */
export const getRhythmPattern = (id) =>
  RHYTHM_PATTERNS.find((pattern) => pattern.id === id) || RHYTHM_PATTERNS[0];

/** The steps a piano plays with its left hand, which it drops while a bass player has the low end. */
export const LEFT_HAND = [BASS, ROOT, FIFTH];

/** Whether a style has a bass player — see `bass` on RHYTHM_PATTERNS. */
export const hasBassLine = (pattern) => Array.isArray(pattern?.bass) && pattern.bass.length > 0;

/** The pad a style plays from Strong up, or null — see `pad` on RHYTHM_PATTERNS. */
export const getPadSoundId = (pattern) => pattern?.pad || null;

/** Whether a style has this extra player — see `band` on RHYTHM_PATTERNS. */
export const hasBandLayer = (pattern, id) => Boolean(pattern?.band?.includes(id));

/** Whether a style's bass line has anything to play at this level, its tags read. */
export const playsBassAt = (pattern, intensity = NORMAL) =>
  hasBassLine(pattern) &&
  pattern.bass.some(({ minLevel = 0, maxLevel = FULL }) => intensity >= minLevel && intensity <= maxLevel);

// Tags for a block-chord bass line, which comes in only once the band does
const AT_STRONG = { minLevel: STRONG, maxLevel: STRONG };
const AT_FULL = { minLevel: FULL };

/**
 * The band under block chords, which are no style of their own: a worship or a praise one per
 * meter, picked by the song's category like the groove. Never offered in the picker. The piano
 * plays the chords as written; these give the bass, pad and band what a style would, and their
 * `steps` are only what the band's players read. The bass line is tagged to Strong and Full, so
 * Normal and below sound as block chords always have.
 */
export const BLOCK_STYLES = [
  {
    id: 'blocks-worship-4',
    cycle: BAR,
    meters: [4],
    drums: 'altar',
    pad: 'warm',
    steps: [{ at: 0, play: CHORD, hold: PEDAL }],
    bass: [
      // Half notes with the kick on 1 and 3, a push into the next bar
      { at: 0, play: ROOT, hold: 2, ...AT_STRONG },
      { at: 2, play: ROOT, hold: 1.5, level: 0.85, ...AT_STRONG },
      { at: 3.5, play: ROOT, hold: 0.5, level: 0.7, ...AT_STRONG },
      // The chorus's driving eighths, leaning on 1 and 3
      ...eighthsOf({ play: ROOT, ...AT_FULL }, [1, 0.6, 0.75, 0.6, 0.9, 0.6, 0.75, 0.7]),
    ],
  },
  {
    id: 'blocks-praise-4',
    cycle: BAR,
    meters: [4],
    drums: 'celebra',
    pad: 'bright',
    band: ['guitar'],
    // The electric guitar's chugs at Full, accented with the kick
    steps: eighthsOf({ play: CHORD }, [1, 0.6, 0.7, 0.85, 0.9, 0.6, 0.7, 0.8]),
    bass: [
      // With the kick: 1, the & of 2 and 3, the fifth above pushing into the next bar
      { at: 0, play: ROOT, hold: 1.25, ...AT_STRONG },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.8, ...AT_STRONG },
      { at: 2, play: ROOT, hold: 1.25, level: 0.9, ...AT_STRONG },
      { at: 3.5, play: TONE, tone: 2, hold: 0.5, level: 0.75, ...AT_STRONG },
      // Pumping eighths, the octave on the last &
      ...eighthsOf({ play: ROOT, ...AT_FULL }, [1, 0.6, 0.75, 0.8, 0.9, 0.6, 0.75]),
      { at: 3.5, play: TONE, tone: 0, octave: 1, hold: 0.5, level: 0.8, ...AT_FULL },
    ],
  },
  {
    id: 'blocks-worship-3',
    cycle: BAR,
    meters: [3],
    drums: 'himno',
    pad: 'warm',
    steps: [{ at: 0, play: CHORD, hold: PEDAL }],
    bass: [
      { at: 0, play: ROOT, hold: 3, ...AT_STRONG },
      { at: 0, play: ROOT, hold: 2, ...AT_FULL },
      { at: 2, play: TONE, tone: 2, hold: 1, level: 0.8, ...AT_FULL },
    ],
  },
  {
    id: 'blocks-praise-3',
    cycle: BAR,
    meters: [3],
    drums: 'jubilo',
    pad: 'strings',
    steps: [{ at: 0, play: CHORD, hold: PEDAL }],
    bass: [
      { at: 0, play: ROOT, hold: 2, ...AT_STRONG },
      { at: 2, play: TONE, tone: 2, hold: 1, level: 0.85, ...AT_STRONG },
      { at: 0, play: ROOT, hold: 1, ...AT_FULL },
      { at: 1, play: ROOT, hold: 0.5, level: 0.7, ...AT_FULL },
      { at: 1.5, play: ROOT, hold: 0.5, level: 0.7, ...AT_FULL },
      { at: 2, play: TONE, tone: 2, hold: 1, level: 0.85, ...AT_FULL },
    ],
  },
  {
    id: 'blocks-worship-6',
    cycle: BAR,
    meters: [6],
    drums: 'mecida',
    pad: 'warm',
    steps: [{ at: 0, play: CHORD, hold: PEDAL }],
    bass: [
      { at: 0, play: ROOT, hold: 6, ...AT_STRONG },
      // Dotted quarters, the fifth above on the second half
      { at: 0, play: ROOT, hold: 3, ...AT_FULL },
      { at: 3, play: TONE, tone: 2, hold: 2, level: 0.85, ...AT_FULL },
      { at: 5, play: ROOT, hold: 1, level: 0.7, ...AT_FULL },
    ],
  },
  {
    id: 'blocks-praise-6',
    cycle: BAR,
    meters: [6],
    drums: 'gozo',
    pad: 'strings',
    steps: [{ at: 0, play: CHORD, hold: PEDAL }],
    bass: [
      { at: 0, play: ROOT, hold: 3, ...AT_STRONG },
      { at: 3, play: TONE, tone: 2, hold: 3, level: 0.85, ...AT_STRONG },
      // With the kicks on 1, 3 and 6
      { at: 0, play: ROOT, hold: 2, ...AT_FULL },
      { at: 2, play: ROOT, hold: 1, level: 0.75, ...AT_FULL },
      { at: 3, play: TONE, tone: 2, hold: 2, level: 0.9, ...AT_FULL },
      { at: 5, play: ROOT, hold: 1, level: 0.75, ...AT_FULL },
    ],
  },
];

/**
 * The band under block chords in this meter, a worship or a praise one by the song's category (none
 * is worship), or null in a meter with neither.
 */
export const getBlockStyle = (beatsPerBar, category = '') => {
  const kind = String(category || '').toLowerCase() === 'praise' ? 'praise' : 'worship';

  return BLOCK_STYLES.find((style) => style.id === `blocks-${kind}-${Number(beatsPerBar)}`) || null;
};

/** Whether a style is offered in a bar of this many beats — see `meters` on RHYTHM_PATTERNS. */
export const fitsMeter = (pattern, beatsPerBar) => pattern.meters.includes(Number(beatsPerBar));

/**
 * The style to play in this meter: the one picked where it fits, and block chords ('') where it
 * doesn't. The pick itself is left alone, so a 4/4 song after a 3/4 one plays it again.
 */
export const getMeterRhythm = (id, beatsPerBar) => (id && fitsMeter(getRhythmPattern(id), beatsPerBar) ? id : '');

/**
 * A stored choice of block chords. A song or part with no choice at all leaves the property off, so
 * picking block chords on purpose needs a value of its own to tell the two apart.
 */
export const BLOCK_CHORDS = 'blocks';

/** The rhythm a part's chart is set to play with: its own, else its song's, else none (null). */
export const getChartRhythm = (part, song) => part?.chartRhythm || song?.chartRhythm || null;

/** A choice — a style id or BLOCK_CHORDS — as the id the players take, '' for block chords. */
export const toRhythmId = (choice, beatsPerBar) => (choice === BLOCK_CHORDS ? '' : getMeterRhythm(choice, beatsPerBar));

/**
 * The groups the styles are offered in, slow to fast.
 *
 * Ordered here rather than where they are drawn: which pace comes first is a statement about the
 * styles, and the row that lays them out should not be the thing deciding it.
 */
export const RHYTHM_PACES = [
  { id: GENTLE, label: 'Gentle' },
  { id: STEADY, label: 'Steady' },
  { id: DRIVING, label: 'Driving' },
];

/**
 * The patterns in those groups, for the picker that offers them.
 *
 * Grouped on the way out rather than by sorting RHYTHM_PATTERNS itself, which cannot move: the
 * default style is the first of that list, and the notes through it navigate by position — "the
 * Salsa above", "the March further up the list".
 *
 * Inside a pace the list's own order is kept, except that styles sharing a groove are pulled into
 * one block. Only Driving needs it, and it needs it badly: the son family and the merengue family
 * both land there, and left interleaved a merengue sits between two halves of the same salsa figure.
 */
export const getRhythmPatternsByPace = () =>
  RHYTHM_PACES.map(({ id, label }) => {
    const patterns = RHYTHM_PATTERNS.filter((pattern) => pattern.pace === id);
    // Where each groove first turns up is where its block goes. Sorting on that alone is enough
    // because the sort is stable, so the styles inside a block stay in the order the list has them.
    const grooves = [...new Set(patterns.map(({ drums }) => drums))];

    return { id, label, patterns: patterns.sort((a, b) => grooves.indexOf(a.drums) - grooves.indexOf(b.drums)) };
  });

/**
 * How long a cycle runs, in beats — the two bar-relative markers resolved against the meter.
 * Exported for the drum grooves, which count their cycles in the same two markers.
 */
export const getCycleBeats = (cycle, beatsPerBar) => {
  if (cycle === BAR) return beatsPerBar;
  if (cycle === TWO_BARS) return beatsPerBar * 2;

  return cycle;
};

// How far under the loudest of its voice a step can sit and still count as an accent
const ACCENT_RANGE = 0.05;

const getVoice = (play) => {
  if (LEFT_HAND.includes(play)) return BASS;

  return play === CHORD ? CHORD : TONE;
};

/**
 * A figure with its soft answers taken out. A step stays if it lands on a strong beat (the
 * downbeat, and the half bar in an even meter) or sits within ACCENT_RANGE of the loudest step its
 * voice plays: the left hand, the chord, or a line. So the shape survives with less in it, and a
 * step alone in its voice is never dropped.
 */
export const getAccentSteps = (steps, beatsPerBar = 4) => {
  const half = beatsPerBar % 2 === 0 ? beatsPerBar / 2 : beatsPerBar;
  const loudest = new Map();

  steps.forEach(({ play, level = 1 }) => {
    const voice = getVoice(play);

    loudest.set(voice, Math.max(loudest.get(voice) ?? 0, level));
  });

  const onStrongBeat = (at) => {
    const off = at % half;

    return off < BEAT_EPSILON || half - off < BEAT_EPSILON;
  };

  return steps.filter(
    ({ at, play, level = 1 }) => onStrongBeat(at) || level >= loudest.get(getVoice(play)) - ACCENT_RANGE - BEAT_EPSILON,
  );
};

/**
 * A pattern laid out across one chord: every step that falls inside it, in beats from its start.
 *
 * Cycles run from the chord's own start rather than from the bar line, which is the rule the whole
 * rhythm path already follows — a change written off the beat carries its pattern off the beat with
 * it, which is what a player does with it too. For a chord starting on a bar line, which is nearly
 * all of them, a BAR cycle lines up with the bars anyway.
 *
 * Each step comes back with the cycle it belongs to, which is what lets a walking hand know whether
 * to carry on or start again — see the note on BASS and ARP above.
 *
 * `thin` plays only the figure's accents (see getAccentSteps), for the quiet levels. A step tagged
 * `minLevel` or `maxLevel` plays only within them at `intensity`, and thinning passes it over. At
 * Sparse a style's own `sparse` figure, where it has one, plays instead, unthinned.
 *
 * `leading` says another chord follows straight on. A step marked `lead: true` then plays in the
 * chord's last cycle only, and one marked `lead: false` everywhere else (see LEAD above).
 *
 * @param {object} pattern - One of RHYTHM_PATTERNS
 * @param {object} span - { beats, beatsPerBar, thin, intensity, leading }
 * @returns {Array<{at, play, hold, level, lead, cycle}>} in beats from the chord's start, ascending
 */
export const getPatternSteps = (
  pattern,
  { beats, beatsPerBar = 4, thin = false, intensity = NORMAL, leading = false } = {},
) => {
  const cycle = getCycleBeats(pattern.cycle, Number(beatsPerBar));
  const steps = [];

  if (!(cycle > 0) || !(beats > 0)) return steps;

  const sparse = intensity === SPARSE && pattern.sparse;
  const source = sparse || pattern.steps;
  const isTagged = (step) => step.minLevel !== undefined || step.maxLevel !== undefined;
  const untagged = source.filter((step) => !isTagged(step));
  const accents = new Set(thin && !sparse ? getAccentSteps(untagged, Number(beatsPerBar)) : untagged);
  const written = source.filter((step) =>
    isTagged(step) ? intensity >= (step.minLevel ?? 0) && intensity <= (step.maxLevel ?? FULL) : accents.has(step),
  );

  for (let start = 0, index = 0; start < beats - BEAT_EPSILON; start += cycle, index += 1) {
    // Only the chord's last turn of the cycle leads into the next one
    const leadsOut = leading && start + cycle >= beats - BEAT_EPSILON;

    written.forEach(({ at, play, hold, level = 1, tone = 0, double = false, octave = 0, alter = 0, dir, lead }) => {
      if (lead !== undefined && lead !== leadsOut) return;

      // A step past the end of its own cycle belongs to a longer bar than this one. A pattern
      // written across four beats simply loses its fourth in 3/4 rather than spilling into the bar
      // after it — which is only offered where `meters` says what's left still reads as the style.
      if (at >= cycle - BEAT_EPSILON) return;

      const beat = start + at;

      if (beat >= beats - BEAT_EPSILON) return;

      // Clipped to whatever is left of the chord, so nothing rings on past the chord it belongs to
      // and into the next
      steps.push({
        at: beat,
        play,
        hold: Math.min(hold, beats - beat),
        level,
        tone,
        double,
        octave,
        alter,
        dir,
        lead,
        cycle: index,
      });
    });
  }

  return steps;
};
